import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAccessToken } from "@/lib/api";
import { useAuth, routeForUser } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Handles the redirect from auth.emergentagent.com.
 * URL has form: <origin>/auth/callback#session_id=...&role=teacher (optional)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    const role = params.get("role"); // 'teacher' | 'student' | null

    if (!sessionId) {
      toast.error("Missing session_id");
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google", {
          session_id: sessionId,
          ...(role ? { role } : {}),
        });
        setAuth(data.access_token, data.user);
        // Clear the fragment so a refresh doesn't replay it.
        window.history.replaceState({}, "", window.location.pathname);
        navigate(routeForUser(data.user), { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Sign-in failed";
        toast.error("Google sign-in failed", { description: String(detail) });
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setAuth]);

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-700">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Completing sign-in…</span>
      </div>
    </div>
  );
}
